import useVuelidate from '@vuelidate/core';
import { helpers, required, requiredIf } from '@vuelidate/validators';
import { BcCalendarCtxObject } from 'bootstrap-vue';
import { dateFormat } from 'commercial-web-client-utilities';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const state = reactive({
    memberId: '',
    ssn: '',
    dob: '',
    firstName: ''
});

export function useForgotUsernameValidations() {
    const { t } = useI18n();

    const firstNameRequired = ref(false);

    const dobDp = ref<Date | null>();

    const validations = {
        memberId: {
            required: requiredIf(() => !state.ssn),
            valid: helpers.regex(/^([A-Za-z0-9\t]{8,20})$/),
        },
        ssn: {
            required: requiredIf(() => !state.memberId),
            valid: helpers.regex(/^[0-9]{3}-[0-9]{2}-[0-9]{4}$/),
        },
        dob: {
            required,
            valid: (value: string) => {
                if (!/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/.test(value)) return false;
                const [month, day, year] = value.split('/').map((value) => parseInt(value) ?? 1);
                const dateOfBirth = new Date(
                    year, month - 1, day
                );
                return (
                    dateOfBirth.getMonth() === month - 1
                    && dateOfBirth.getDate() === day
                    && dateOfBirth.getFullYear() === year
                );
            },
        },
        firstName: {
            required: requiredIf(() => firstNameRequired.value)
        }
    };

    const errorMessages = {
        memberId: {
            valid: t('forgotUsername.infoForm.validations.invalidIdCard'),
        },
        ssn: {
            valid: t('forgotUsername.infoForm.validations.invalidSsn'),
        },
        memberIdOrSSNRequired: {
            required: t('forgotUsername.infoForm.validations.requiredMemberIdOrSSN'),
        },
        bothMemberIdAndSSNInput: {
            valid: t('forgotUsername.infoForm.alerts.bothIdAndSSN'),
        },
        dob: {
            required: t('forgotUsername.infoForm.validations.requiredDob'),
            valid: t('forgotUsername.infoForm.validations.invalidDob'),
        },
        firstName: {
            required: t('forgotUsername.infoForm.validations.requiredFirstName')
        }
    };

    const reset = () => {
        state.memberId = '';
        state.ssn = '';
        state.dob = '';
        state.firstName = '';
        firstNameRequired.value = false;
        v$.value.$reset();
    };

    const touch = () => {
        v$.value.memberId.$touch();
        v$.value.ssn.$touch();
        v$.value.dob.$touch();
        v$.value.firstName.$touch();
    };

    const errors = computed(() => {
        return {
            memberIdValid: v$.value.memberId.valid.$invalid ? errorMessages.memberId.valid : null,
            ssnValid: v$.value.ssn.valid.$invalid ? errorMessages.ssn.valid : null,
            dobRequired: v$.value.dob.required.$invalid ? errorMessages.dob.required : null,
            dobInvalid: v$.value.dob.valid.$invalid ? errorMessages.dob.valid : null,
            memberIdOrSSNRequired: (v$.value.memberId.required.$invalid && v$.value.ssn.required.$invalid)
                ? errorMessages.memberIdOrSSNRequired.required
                : null,
            bothMemberIdAndSSNInput: (state.memberId.length > 0 && state.ssn.length > 0)
                ? errorMessages.bothMemberIdAndSSNInput.valid
                : null,
            firstNameRequired: v$.value.firstName.required.$invalid ? errorMessages.firstName.required : null
        };
    });

    const onDobContextChange = (ctx: BcCalendarCtxObject) => {
        if (ctx.selectedDate) {
            state.dob = dateFormat(ctx.selectedDate, 'MM/DD/YYYY');
        }
    };

    const syncDob = () => {
        if (!v$.value.dob.$invalid) {
            const date: Date = new Date(state.dob);
            dobDp.value = date;
            state.dob = dateFormat(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
        }
    };

    const validateState = (fieldName: keyof typeof state) => {
        const field = v$.value[fieldName];
        if (field && field.$dirty && field.$error) {
            return !field.$error;
        } else {
            return null;
        }
    };

    const v$ = useVuelidate(validations, state);

    return {
        state,
        v$,
        errorMessages,
        errors,
        reset,
        validateState,
        onDobContextChange,
        syncDob,
        firstNameRequired,
        dobDp,
        touch
    };
}
